/**
 * <blog-post-toc> - compact "On this page" list built from the article's headings.
 *
 * - Shows only the levels chosen in the block settings (default: H2 only).
 * - Shows the first N entries, with a "Show all sections" toggle for the rest,
 *   so a long article never turns into a wall of links.
 * - Highlights the section currently being read.
 * - Stays hidden until it is built, so a script error never leaves an empty box.
 */
class BlogPostTOC extends HTMLElement {
  connectedCallback() {
    try {
      this.init();
    } catch (error) {
      console.error('blog-post-toc:', error);
    }
  }

  init() {
    this.listEl = this.querySelector('[data-toc-list]');
    this.moreBtn = this.querySelector('[data-toc-more]');
    if (!this.listEl) return;

    const scope = this.closest('.section-content-wrapper') || document;
    this.contentEl = scope.querySelector(this.dataset.contentSelector || '.blog-post-content');
    if (!this.contentEl) return;

    const levels = (this.dataset.levels || 'h2')
      .split(',')
      .map((l) => l.trim())
      .filter(Boolean)
      .join(',');

    const headings = Array.from(this.contentEl.querySelectorAll(levels || 'h2')).filter(
      (h) => h.textContent.trim().length > 0
    );

    // Not worth a table of contents for very short articles.
    const minItems = parseInt(this.dataset.minItems || '3', 10);
    if (headings.length < minItems) return;

    this.buildList(headings);
    this.setupToggle();
    this.observeHeadings(headings);
    this.trackContentEnd();
    this.setAttribute('data-ready', '');
  }

  slugify(text, used) {
    let slug = (text || '')
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
    if (!slug) slug = 'section';
    let unique = slug;
    let i = 1;
    while (used.has(unique)) {
      unique = `${slug}-${i}`;
      i += 1;
    }
    used.add(unique);
    return unique;
  }

  buildList(headings) {
    const used = new Set();
    const root = document.createElement('ol');
    root.className = 'blog-post-toc__list';
    const stack = [{ level: 2, list: root }];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    headings.forEach((heading) => {
      if (!heading.id) heading.id = this.slugify(heading.textContent, used);
      else used.add(heading.id);

      const level = parseInt(heading.tagName.substring(1), 10) || 2;

      while (stack.length > 1 && level < stack[stack.length - 1].level) stack.pop();

      if (level > stack[stack.length - 1].level) {
        const parentLi = stack[stack.length - 1].list.lastElementChild;
        if (parentLi) {
          const sub = document.createElement('ol');
          sub.className = 'blog-post-toc__sublist';
          parentLi.appendChild(sub);
          stack.push({ level, list: sub });
        }
      }

      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${heading.id}`;
      a.textContent = heading.textContent.trim();
      a.dataset.tocTarget = heading.id;
      a.addEventListener('click', (event) => {
        event.preventDefault();
        heading.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', `#${heading.id}`);
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      });

      li.appendChild(a);
      stack[stack.length - 1].list.appendChild(li);
    });

    this.listEl.replaceWith(root);
    this.listEl = root;
    this.links = Array.from(root.querySelectorAll('a'));
  }

  setupToggle() {
    const items = Array.from(this.listEl.children);
    const visible = parseInt(this.dataset.visible || '6', 10);
    if (!this.moreBtn || items.length <= visible + 1) return;

    const hiddenItems = items.slice(visible);
    const showLabel = (this.dataset.moreLabel || 'Show all sections') + ` (${items.length})`;
    const hideLabel = this.dataset.lessLabel || 'Show fewer';

    const apply = (expanded) => {
      hiddenItems.forEach((li) => (li.hidden = !expanded));
      this.moreBtn.textContent = expanded ? hideLabel : showLabel;
      this.moreBtn.setAttribute('aria-expanded', String(expanded));
    };

    this.moreBtn.hidden = false;
    this.moreBtn.addEventListener('click', () => {
      apply(this.moreBtn.getAttribute('aria-expanded') !== 'true');
    });
    apply(false);
  }

  observeHeadings(headings) {
    if (!('IntersectionObserver' in window) || !this.links || !this.links.length) return;

    const setActive = (id) => {
      this.links.forEach((link) => {
        const on = link.dataset.tocTarget === id;
        link.classList.toggle('is-active', on);
        if (on) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    };

    const seen = new Set();
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) seen.add(entry.target.id);
          else seen.delete(entry.target.id);
        });
        const first = headings.find((h) => seen.has(h.id));
        if (first) setActive(first.id);
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 }
    );
    headings.forEach((h) => this.observer.observe(h));
  }

  // Fade the side rail out once the reader has scrolled past the article body,
  // so it never sits on top of the share, author and related-posts areas.
  trackContentEnd() {
    if (!('IntersectionObserver' in window)) return;
    this.endObserver = new IntersectionObserver(
      ([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.toggleAttribute('data-past', past);
      },
      { threshold: 0 }
    );
    this.endObserver.observe(this.contentEl);
  }

  disconnectedCallback() {
    if (this.endObserver) this.endObserver.disconnect();
    if (this.observer) this.observer.disconnect();
  }
}

if (!customElements.get('blog-post-toc')) {
  customElements.define('blog-post-toc', BlogPostTOC);
}
